import React, { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { c } from 'ttag';

import {
    Alert,
    AuthStep,
    AuthenticationStore,
    Button,
    Divider,
    Modal,
    useAuthentication,
    useLocalState,
} from '@freelancelabs/components';
import { KeyOutline } from '@freelancelabs/icons';

import PublicLayout from '../app/content/PublicLayout';
import EmailOtpForm from '../app/login/EmailOtpForm';
import LoginForm from '../app/login/LoginForm';
import SetPasswordForm from '../app/login/SetPasswordForm';
import TotpCodeForm from '../app/login/TotpCodeForm';
import { AlertType, alertMessages, defaultUsernameKey } from '../constants';

const LoginPage = () => {
    const location = useLocation();
    const { completeNewPassword, confirmMfaCode, login } = useAuthentication() as AuthenticationStore;
    const [username, setUsername] = useLocalState('', defaultUsernameKey);
    const [step, setStep] = useState(AuthStep.LOGIN);
    // Password kept during the MFA step so a code can be resent.
    const [mfaPassword, setMfaPassword] = useState('');
    const [mfaChallenge, setMfaChallenge] = useState<'EMAIL_OTP' | 'TOTP_CODE'>('EMAIL_OTP');
    const [searchParams] = useSearchParams();

    // The alert comes from the router or from the URL (the second path for cross-app redirects,
    // where location.state does not survive). The value is verified against the enum because
    // alertMessages() throws on an unknown key, which would break this entry screen.
    const depuisUrl = searchParams.get('alerte');
    const alert: AlertType =
        location.state?.withAlert ?? (depuisUrl && depuisUrl in AlertType ? (depuisUrl as AlertType) : undefined);

    return (
        <PublicLayout>
            {alert && (
                <Alert
                    type="danger"
                    title={alertMessages()[alert].title}
                    message={alertMessages()[alert].message}
                    className="mb-4"
                />
            )}
            {/* The form stays mounted under the MFA modal, which sits over it; unmounted it left an
                empty column behind the box. NEW_PASSWORD is not in this condition: it replaces the
                screen rather than covering it. */}
            {(step === AuthStep.LOGIN || step === AuthStep.SSO_EMAIL || step === AuthStep.MFA_CODE) && (
                <>
                    {/* SSO toggle: it changes the state of the same form rather than navigating, so
                        the email field keeps its value. */}
                    <Button
                        size="large"
                        category="weak"
                        onClick={() => setStep(step === AuthStep.SSO_EMAIL ? AuthStep.LOGIN : AuthStep.SSO_EMAIL)}
                    >
                        <KeyOutline />
                        {step === AuthStep.SSO_EMAIL
                            ? c('Sso').t`Continue with password`
                            : c('Sso').t`Continue with SSO`}
                    </Button>
                    {/* The passwordless button is removed from the screen, but the route, its page and
                        passwordLessLogin stay in place, so the emailed link still works. */}
                    <Divider className="my-8">{c('Divider').t`or`}</Divider>
                    <LoginForm
                        defaultUsername={username}
                        ssoMode={step === AuthStep.SSO_EMAIL}
                        onUsernameChange={setUsername}
                        onSubmit={(data) => {
                            setUsername(data.payload.username);
                            if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
                                setStep(AuthStep.NEW_PASSWORD);
                            }
                            if (data.challenge === 'EMAIL_OTP' || data.challenge === 'TOTP_CODE') {
                                setMfaChallenge(data.challenge);
                                setMfaPassword(data.payload.password);
                                setStep(AuthStep.MFA_CODE);
                            }
                        }}
                    />
                </>
            )}
            <Modal
                open={step === AuthStep.MFA_CODE}
                onClose={() => setStep(AuthStep.LOGIN)}
                title={c('Mfa title').t`Security verification`}
                size={560}
                panelProps={{ className: 'modal_panel-container mfa-modal' }}
            >
                {mfaChallenge === 'EMAIL_OTP' ? (
                    <EmailOtpForm
                        email={username}
                        onSubmit={(code) => confirmMfaCode(code)}
                        onResend={async () => {
                            await login({ email: username, password: mfaPassword });
                        }}
                        onCancel={() => setStep(AuthStep.LOGIN)}
                    />
                ) : (
                    <TotpCodeForm onSubmit={(code) => confirmMfaCode(code)} onCancel={() => setStep(AuthStep.LOGIN)} />
                )}
            </Modal>
            {step === AuthStep.NEW_PASSWORD && (
                <>
                    <h1 className="text-2xl font-semibold text-text-norm pb-2">
                        {c('New password required title').t`Create a new password`}
                    </h1>
                    <span className="text-text-norm pb-6">
                        {c('New password required help').t`For security reasons you must set a new password.`}
                    </span>
                    <SetPasswordForm
                        username={username}
                        onSubmit={(newPassword) => {
                            return completeNewPassword(newPassword);
                        }}
                    />
                </>
            )}
        </PublicLayout>
    );
};
export default LoginPage;
